/**
 * **Margen del kiosko**: margen actual, ganancia por unidad y precio sugerido para el
 * margen objetivo de cada producto (pedido de Damián, 23-oct-2026: «avisos de ajustes de
 * precio cuando pone un precio por debajo de lo que lo compró o cuando el margen es muy
 * bajo»).
 *
 * Funciones puras, en centavos: las usan el backend (al guardar un precio y después de
 * cada compra) y la SPA (el cálculo en vivo del modal de producto y el resumen de una
 * compra nueva), así las dos dicen siempre lo mismo.
 *
 * Margen = (precio − costo) / precio. Un costo `≤ 0` es «nunca se compró» (no «cuesta $0»)
 * y un precio `≤ 0` no tiene margen: en los dos casos el margen es `null`, nunca un 0
 * inventado.
 */
/** Tope del margen objetivo: con 100 % el precio sugerido sería infinito. */
export const TARGET_MARGIN_MAX_PCT = 95;
/** El precio sugerido se redondea hacia arriba a $10 (1.000 centavos). */
export const SUGGESTED_PRICE_STEP = 1_000;
/** Margen actual en %, con un decimal (`37.5` = 37,5 %). `null` = no se puede calcular. */
export function marginPctOf(price, cost) {
    if (cost === null || cost === undefined || cost <= 0 || price <= 0)
        return null;
    return Math.round(((price - cost) / price) * 1000) / 10;
}
/** Ganancia por unidad (centavos). `null` = sin costo conocido. */
export function unitProfitOf(price, cost) {
    if (cost === null || cost === undefined || cost <= 0)
        return null;
    return price - cost;
}
/**
 * Precio que deja el margen objetivo con el costo dado: `costo / (1 − objetivo/100)`,
 * redondeado **hacia arriba** al múltiplo de `step` (default $10). `null` si no hay costo
 * o no hay objetivo.
 *
 * Aritmética entera (el objetivo se toma con un decimal): `60000 / (1 − 0,4)` en coma
 * flotante da `100000,00000000001` y redondear eso hacia arriba sugeriría $10 de más justo
 * en los números redondos.
 */
export function suggestedPriceOf(cost, targetMarginPct, step = SUGGESTED_PRICE_STEP) {
    if (cost === null || cost === undefined || cost <= 0)
        return null;
    if (targetMarginPct === null || targetMarginPct === undefined)
        return null;
    if (targetMarginPct < 0 || targetMarginPct > TARGET_MARGIN_MAX_PCT)
        return null;
    const tenths = Math.round(targetMarginPct * 10);
    // precio = costo × 1000 / (1000 − objetivo en décimas), redondeado hacia arriba a `step`.
    const numerator = Math.round(cost) * 1000;
    const denominator = (1000 - tenths) * step;
    // Cociente de enteros: si es exacto, la división en coma flotante también lo es.
    return Math.ceil(numerator / denominator) * step;
}
/**
 * ¿El precio hay que revisarlo? `below_cost` si se vende por debajo del último costo;
 * `low_margin` si el margen quedó por debajo del objetivo del producto. Sin costo conocido
 * no hay nada que revisar.
 */
export function priceReviewOf(input) {
    const marginPct = marginPctOf(input.price, input.cost);
    const unitProfit = unitProfitOf(input.price, input.cost);
    const suggestedPrice = suggestedPriceOf(input.cost, input.targetMarginPct);
    const cost = input.cost ?? 0;
    let reason = null;
    if (cost > 0 && input.price < cost)
        reason = "below_cost";
    else if (isBelowTargetMargin(input))
        reason = "low_margin";
    return { reason, marginPct, unitProfit, suggestedPrice };
}
/**
 * ¿El margen quedó por debajo del objetivo? Independiente de `priceReviewOf().reason`: un
 * precio bajo costo con objetivo cargado también está bajo el objetivo (y lleva su aviso
 * `LOW_MARGIN`), aunque el motivo principal de revisión sea `below_cost`. Sin costo
 * conocido o sin objetivo, `false`.
 */
export function isBelowTargetMargin(input) {
    const cost = input.cost ?? 0;
    if (input.targetMarginPct === null || input.targetMarginPct === undefined || cost <= 0) {
        return false;
    }
    // Margen exacto (sin el redondeo a un decimal de `marginPct`) contra el objetivo.
    return (input.price - cost) * 100 < input.targetMarginPct * input.price;
}
//# sourceMappingURL=kiosk-margins.js.map